// Global Instructions Rule Applied!
//
// "Which round?" — the modal that ticking "Is Funding Round" on an EXISTING
// deal opens (owner instruction 2026-09-21).
//
// Before this, ticking the box made the backend mint a round out of nothing:
// no name, no currency, no close date. Those rows render as "Unnamed round"
// and kept reattaching themselves to deals in PROD. A round belongs to the
// company and is created deliberately — so the tick now asks, offering the two
// affordances the deal Execution tab's Funding Round card already offers
// inline: link one of the company's rounds, or create a new named one.
//
// Resolves through `onPicked(roundId)`; `onCancel` means the box stays
// unticked and nothing is written.
import React, { useEffect, useState } from 'react'
import { Button, Modal, Select, Spin } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { fetchAccountDealsRounds } from '../../deal_execution/services/dealExecutionService.ts'
import AddRoundModal from './AddRoundModal.tsx'

interface LinkOrCreateRoundModalProps {
  open: boolean
  /** The deal's company — rounds are listed for, and created against, it. */
  companyId: string | null
  companyName?: string | null
  defaultCurrencyId?: string | null
  onPicked: (roundId: string) => void
  onCancel: () => void
}

export default function LinkOrCreateRoundModal({
  open,
  companyId,
  companyName,
  defaultCurrencyId,
  onPicked,
  onCancel
}: LinkOrCreateRoundModalProps): React.ReactElement {
  const [rounds, setRounds] = useState<Array<Record<string, unknown>>>([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [newRoundOpen, setNewRoundOpen] = useState(false)

  // The company's rounds come from the read the Deals & Rounds tab already
  // uses — no new RPC for a picker.
  useEffect(() => {
    if (!open || !companyId) return
    let cancelled = false
    setSelected(null)
    setLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const result = await fetchAccountDealsRounds(token, companyId)
      if (cancelled) return
      setRounds(result.data.rounds || [])
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [open, companyId])

  const options = rounds.map((round) => {
    const closeDate = round.rounds__round_close_date as string | null | undefined
    return {
      value: round.rounds__id as string,
      label: `${(round.rounds__name as string) || 'Unnamed round'}${
        closeDate ? ` · ${new Date(closeDate).toLocaleDateString()}` : ''
      }`
    }
  })

  return (
    <>
      <Modal
        title='Link a funding round'
        open={open}
        okText='Link round'
        okButtonProps={{ disabled: !selected }}
        onOk={() => {
          if (selected) onPicked(selected)
        }}
        onCancel={onCancel}
        width={520}
      >
        <div className='text-sm text-gray-500 mb-3'>
          A round belongs to the company, and a deal links to it. Pick one of
          {companyName ? ` ${companyName}'s` : ' the company’s'} rounds, or create a new one.
        </div>
        <div className='flex gap-2 items-center'>
          {loading ? (
            <Spin size='small' />
          ) : (
            <Select
              placeholder={options.length ? 'Company rounds…' : 'No rounds on this company yet'}
              className='flex-1 min-w-0'
              value={selected ?? undefined}
              onChange={(value) => setSelected(value)}
              options={options}
              disabled={!options.length}
              showSearch
              optionFilterProp='label'
            />
          )}
          <Button icon={<PlusOutlined />} disabled={!companyId} onClick={() => setNewRoundOpen(true)}>
            New round
          </Button>
        </div>
      </Modal>

      <AddRoundModal
        open={newRoundOpen}
        companyId={companyId}
        companyName={companyName}
        defaultCurrencyId={defaultCurrencyId}
        onCancel={() => setNewRoundOpen(false)}
        onCreated={(roundId) => {
          setNewRoundOpen(false)
          if (!roundId) {
            showErrorToast('The round was created but could not be linked — link it from the picker')
            return
          }
          onPicked(roundId)
        }}
      />
    </>
  )
}
